import { FormGroup } from "../../../../components/forms";
import { DateInput, RadioInput, NumberInput } from "../../../../components/form-fields";
import { FC, useState } from "react";
import { FieldChange, TravelDetails } from "../../../../types/travel/Policy";
import { H2 } from "../../../../components/basics";
import { detailsValidations } from "../../../../helpers/travel/validations";
import useForm from "../../../../hooks/useForm";
import { useTranslation } from "react-i18next";
import classNamePresets from "../../../../utils/classNamePresets";
import PolicyFormStepNavigation from "../PolicyFormStepNavigation";
import { PolicyDetailsMoreInfo } from "../more-info/PolicyDetailsMoreInfo";

export interface DetailsProps {
  flowStateSlice: TravelDetails;
  onFieldChange: FieldChange<TravelDetails>;
  onNext: () => void;
}

const Details: FC<React.PropsWithChildren<DetailsProps>> = ({ flowStateSlice, onFieldChange, onNext }) => {
  const { t } = useTranslation("pages", { keyPrefix: "travel.newPolicy.steps.details" });

  const isAnnual = flowStateSlice.policyType === "annual-trip";
  const isFamily = flowStateSlice.groupType === "family";
  const isSingleIndividual = !isAnnual && !isFamily;
  const { validateField, validateForm, field, handleFieldChange, clearErrors } = useForm(flowStateSlice, detailsValidations, onFieldChange);
  const handleNext = () => validateForm(onNext);
  const [moreInfo, setMoreInfo] = useState<string>("");

  const handleDateChange: FieldChange<TravelDetails> = (key, value) => {
    handleFieldChange(key, value);
    validateField(key, value);
  };

  return (
    <>
      <div className="container-flow flex-grow">
        <H2 className={classNamePresets.mainHeading}>{t("title")}</H2>
        <FormGroup
          title={t("form.groupTitle.policyType")}
          subtitle={t("form.groupTitle.subTitle.covidCoverage")}
          onMoreInfo={() => setMoreInfo("trip-type")}
        >
          <RadioInput
            aria-label="policy type"
            {...field.policyType}
            value={flowStateSlice.policyType}
            options={[
              { value: "single-trip", label: t("form.label.singleTrip") },
              { value: "annual-trip", label: t("form.label.annualTrip") },
            ]}
            onValueChange={v => {
              field.policyType.onValueChange(v);
              clearErrors();
            }}
          />
        </FormGroup>
        <FormGroup title={t("form.groupTitle.groupType")} onMoreInfo={() => setMoreInfo("travellers")}>
          <RadioInput
            aria-label="group Type"
            {...field.groupType}
            value={flowStateSlice.groupType}
            options={[
              { value: "individual", label: isAnnual ? t("form.label.individual") : t("form.label.individualGroup") },
              { value: "family", label: t("form.label.family") },
            ]}
            onValueChange={v => {
              field.groupType.onValueChange(v);
              clearErrors();
            }}
          />
        </FormGroup>
        {flowStateSlice.groupType && (
          <>
            <FormGroup
              title={isFamily || isSingleIndividual ? t("form.groupTitle.adults") : t("form.groupTitle.travellers")}
              onMoreInfo={isFamily || isSingleIndividual ? () => setMoreInfo("family-travellers") : undefined}
            >
              <NumberInput
                {...field.adultCount}
                value={flowStateSlice.adultCount}
                label={isFamily || isSingleIndividual ? t("form.groupTitle.adults") : t("form.groupTitle.travellers")}
                min={isSingleIndividual ? 0 : 1}
                max={isSingleIndividual ? 10 : isFamily ? 2 : 1}
                onValueChange={v => {
                  handleDateChange("adultCount", v);
                  field.adultCount.onValueChange(v);
                  if (isSingleIndividual) {
                    validateField("childrenCount", flowStateSlice.childrenCount, { adultCount: v });
                  }
                }}
              />
            </FormGroup>
            {(isFamily || isSingleIndividual) && (
              <FormGroup title={t("form.groupTitle.children")}>
                <NumberInput
                  {...field.childrenCount}
                  value={flowStateSlice.childrenCount}
                  min={0}
                  max={10}
                  label={t("form.label.children")}
                  onValueChange={v => {
                    handleDateChange("childrenCount", v);
                    field.childrenCount.onValueChange(v);
                    validateField("adultCount", flowStateSlice.adultCount, { childrenCount: v });
                  }}
                />
              </FormGroup>
            )}
          </>
        )}
        <FormGroup title={t("form.groupTitle.region")} onMoreInfo={() => setMoreInfo("travel-region")}>
          <RadioInput
            aria-label="location"
            {...field.location}
            value={flowStateSlice.location}
            options={[
              { value: "asean", label: t("form.label.asean"), visible: !isAnnual },
              { value: "asia-pacific", label: t("form.label.asiaPacific") },
              { value: "worldwide", label: t("form.label.worldwide") },
            ]}
          />
        </FormGroup>
        <FormGroup title={t("form.groupTitle.startDate")} hasVerticalFieldsOrientation>
          <DateInput
            {...field.startDate}
            value={flowStateSlice.startDate}
            minDate={new Date()}
            label={isAnnual ? t("form.label.startDate.annual") : t("form.label.startDate.single")}
            onValueChange={v => {
              handleDateChange("startDate", v);
              field.startDate.onValueChange(v);

              // trigger end date validation
              if (flowStateSlice.endDate) {
                validateField("endDate", flowStateSlice.endDate, { startDate: v });
              }
            }}
          />
        </FormGroup>
        {!isAnnual && (
          <FormGroup title={t("form.groupTitle.endDate")} hasVerticalFieldsOrientation={true}>
            <DateInput
              {...field.endDate}
              value={flowStateSlice.endDate}
              minDate={flowStateSlice.startDate ? new Date(flowStateSlice.startDate) : new Date()}
              onValueChange={value => {
                handleDateChange("endDate", value);
                field.endDate.onValueChange(value);

                validateField("endDate", value, { startDate: flowStateSlice.startDate });
              }}
              label={t("form.label.endDate.single")}
            />
          </FormGroup>
        )}
      </div>

      <PolicyDetailsMoreInfo contentType={moreInfo} show={moreInfo !== ""} onClose={() => setMoreInfo("")} />
      <PolicyFormStepNavigation onNext={handleNext} />
    </>
  );
};

export default Details;
